import { Injectable } from '@angular/core';
import { BluetoothSerial } from '@ionic-native/bluetooth-serial/ngx';
import { Subject, Subscription } from 'rxjs';
import { Printer } from 'src/app/models/printer/printer.model';
import { IPrinterStatus } from './printer.interface';

import EscPosEncoder from 'esc-pos-encoder';

import { PRINTER_STATUS } from './printer.constants';
import { UtilityClass } from '../../utils/UtilityClass';

@Injectable({
  providedIn: 'root',
})
export class BluetoothPrinterStatusService {
  public DLE_EOT_fn: number = 1;
  public DLE_EOT_waiting: boolean = false;

  // public GS_a_timeout;
  public DLE_EOT_timeout;

  // the current printer being checked
  public printerToCheck: Printer;

  // The subscription reference of the bluetooth serial raw data listener
  private _subscribeBluetoothRawData: Subscription;

  // Emits the status check response
  private _printerStatusReady: Subject<IPrinterStatus> = new Subject<IPrinterStatus>();

  constructor(public bluetooth: BluetoothSerial) {}

  /**
   * Starts the flow of printer status check
   * @param printer - The printer to check
   * @returns A promise of the printer status
   */
  checkPrinterStatus(printer: Printer, timeout: number = 10000): Promise<IPrinterStatus> {
    return new Promise(async resolve => {
      if (!this.printerToCheck) {
        this.printerToCheck = printer;
        this._startBluetoothPrinterStatusCheck(timeout).then(result => {
          resolve(result);
        });
      } else {
        resolve({
          success: false,
          status: PRINTER_STATUS.BUSY,
        });
        // send a bluetooth busy response
      }
    });
  }

  /**
   * Initialises the bluetooth module for a printer status check
   * @returns A promise of the printer status
   */
  private _startBluetoothPrinterStatusCheck(timeout: number): Promise<IPrinterStatus> {
    return new Promise(resolve => {
      const printerStatusReadySub = this._printerStatusReady.subscribe(async status => {
        if (printerStatusReadySub) printerStatusReadySub.unsubscribe();
        if (this._subscribeBluetoothRawData) this._subscribeBluetoothRawData.unsubscribe();
        clearTimeout(this.DLE_EOT_timeout);

        await this._bluetoothRead();
        this.reset();

        // now remove the printer being checked
        this.printerToCheck = null;

        // now send back the status pulled
        resolve(status);
      });

      // establish the status check listener
      this._listenToBluetoothStatusCheck();

      // triggers the status check write
      this._sendStatusCheck(1);

      // assume the printer is unreachable if ping is longer than 15 seconds
      clearTimeout(this.DLE_EOT_timeout);

      this.DLE_EOT_timeout = setTimeout(() => {
        console.log('PRINTER UNREACHABLE, TIMEOUT');

        // ASSUME PRINTER UNREACHABLE
        this._printerStatusReady.next({
          success: false,
          status: PRINTER_STATUS.UNREACHABLE,
          statusFn: this.DLE_EOT_fn,
        });

        this.reset();
      }, timeout);
    });
  }

  /**
   * Sends the status request to the printer using the DLE EOT function
   * @param status The printer status function to request
   */
  private async _sendStatusCheck(status: number) {
    if (!this.DLE_EOT_waiting) {
      clearTimeout(this.DLE_EOT_timeout);

      // block any incoming requests
      this.DLE_EOT_waiting = true;
      this.DLE_EOT_fn = status;

      const encoder = new EscPosEncoder();
      let result = encoder
        .initialize()
        .raw([0x10, 0x04, status]) // DLE EOT
        .encode();

      try {
        this.printerToCheck.updateStatus({
          success: true,
          status: PRINTER_STATUS.CHECKING,
          statusFn: status,
        });

        // send the status check request
        this.bluetooth.write(result).then(
          result => {
            // just wait
          },
          err => {
            this._printerStatusReady.next({
              success: false,
              status: PRINTER_STATUS.UNKNOWN,
              statusFn: this.DLE_EOT_fn,
            });
          },
        );
      } catch (err) {
        this._printerStatusReady.next({
          success: false,
          status: PRINTER_STATUS.UNKNOWN,
          statusFn: this.DLE_EOT_fn,
        });

        this.reset();
      }
    }
  }

  /**
   * Creates the subscription to listen to the response data from the printer
   * @returns A promise of printer status
   */
  private _listenToBluetoothStatusCheck() {
    this._subscribeBluetoothRawData = this.bluetooth.subscribeRawData().subscribe(successSubscribe => {
      if (successSubscribe) {
        const printerBits = new Int8Array(successSubscribe);
        if (printerBits) {
          // this is a DLE EOT call
          const binarySet: number[][] = UtilityClass.convertIntArrayToBinaries(printerBits);
          if (printerBits.length > 1) {
            console.log('GS a: BITS', binarySet);
          } else if (printerBits.length == 1) {
            // if not expecting a response, do not process
            // instead fire a new request
            if (!this.DLE_EOT_waiting) {
              this.reset();
              return;
            }

            // this is a DLE EOT call
            if (binarySet && binarySet.length > 0) {
              if (binarySet[0][0] == 0 && binarySet[0][1] == 1 && binarySet[0][4] == 1 && binarySet[0][7] == 0) {
                const statusCheckResult: IPrinterStatus = Printer.AnalysePrinterStatus(binarySet[0], this.DLE_EOT_fn);

                if (!statusCheckResult.success) {
                  if (this.DLE_EOT_fn == 1) {
                    if (statusCheckResult.status == PRINTER_STATUS.OFFLINE) {
                      this.DLE_EOT_waiting = false;
                      this._sendStatusCheck(2);
                    } else {
                      this._printerStatusReady.next(statusCheckResult);
                    }
                  } else if (this.DLE_EOT_fn == 2) {
                    if (statusCheckResult.status == PRINTER_STATUS.ERROR_OCCURRED) {
                      this.DLE_EOT_waiting = false;
                      this._sendStatusCheck(3);
                    } else {
                      this._printerStatusReady.next(statusCheckResult);
                    }
                  } else if (this.DLE_EOT_fn == 3) {
                    this._printerStatusReady.next(statusCheckResult);
                  }
                } else {
                  this._printerStatusReady.next(statusCheckResult);
                }
              }
            }
          }
        }
      }
    });
  }

  /** Read the data in the bluetooth stream */
  private _bluetoothRead(): Promise<any> {
    return new Promise(resolve => {
      this.bluetooth.read().then(
        successRead => {
          this._clearBluetoothBuffer().then(() => resolve(true));
        },
        failRead => {
          resolve(false);
        },
      );
    });
  }

  /** Clears the bluetooth buffer stream */
  private _clearBluetoothBuffer(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.bluetooth.clear().then(
        successClear => {
          resolve(successClear);
        },
        failClear => {
          resolve(failClear);
        },
      );
    });
  }

  /** Resets the state of the service */
  public reset() {
    this.DLE_EOT_fn = 1;
    this.DLE_EOT_waiting = false;
  }
}
